/**
 * Safe narrowing helpers for the untyped `details` / `context` JSONB blobs
 * (typed `Record<string, unknown>` on the wire). Variants read renderable
 * content out of these blobs through these guards, never via `as` casts.
 * No `any`. (Unused general accessors were trimmed with the variant prune.)
 *
 * D14: oversized string blobs are replaced server-side with a truncation
 * marker `{ _truncated: true, _original_bytes: N }` — `isTruncated` detects it.
 */

export function asString(v: unknown): string | null {
  return typeof v === 'string' ? v : null
}

export function asNumber(v: unknown): number | null {
  return typeof v === 'number' ? v : null
}

/** The string members of a list; a value that is not a list has none. */
export function asStrings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((member): member is string => typeof member === 'string') : []
}

export function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null
}

/** D14 truncation marker shape. */
export interface TruncationMarker {
  _truncated: true
  _original_bytes: number
}

export function isTruncated(v: unknown): v is TruncationMarker {
  const r = asRecord(v)
  return r !== null && r._truncated === true && typeof r._original_bytes === 'number'
}

/** Human-readable size of a truncated blob (e.g. "32.0 KB"). */
export function truncatedSize(m: TruncationMarker): string {
  return `${(m._original_bytes / 1024).toFixed(1)} KB`
}
